package com.exura.features.plandefinition.presentation

import androidx.compose.runtime.*
import com.exura.components.FlexContent
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraBodySizeTextVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.HoverableListEntrySurfaceVariant
import com.exura.components.pageTypography.ListEntryImageNoImageVariant
import com.exura.components.pageTypography.ListEntryImageStyle
import com.exura.components.pageTypography.ListEntryKind
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.planLocalization
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.exura.util.lineClamp
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.AspectRatio
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times

@Composable
fun PlanDefinitionListEntry(
    version: Version<BasePlan, PlanDefinitionMetadata>,
    variant: CssStyleVariant<ListEntryKind>? = HoverableListEntrySurfaceVariant,
    onClick: (() -> Unit)? = null,
) = GeneralListEntry(
    modifier = Modifier
        .fillMaxWidth()
        .maxHeight(Dimens.listEntryImageSize + 2 * Dimens.listEntryWrapping)
        .onClick { onClick?.invoke() },
    variant = variant,
) {
    val metadata = version.metadata
    val fields = metadata?.fields
    val statistics = metadata?.statistics

    Box(
        modifier = ListEntryImageStyle
            .toModifier()
    ) {
        fields?.imageUrl?.let { url ->
            if (url.isNotEmpty()) {
                Image(src = url, Modifier.aspectRatio(AspectRatio.Auto).fillMaxSize())
            }
        }
    }

    PlanDefinitionDetails(
        modifier = Modifier.weight(1f),
        title = fields?.title ?: "",
        description = fields?.description ?: "",
        versionId = version.firebaseDocId,
        planDuration = statistics?.planDuration ?: 0,
        numberInstruction = statistics?.numberInstruction ?: 0,
        numberPersistent = statistics?.numberPersistent ?: 0,
        numberQuestion = statistics?.numberQuestion ?: 0,
        numberFloating = statistics?.numberFloating ?: 0,
    )
}

@Composable
fun PlanDefinitionDetails(
    modifier: Modifier = Modifier,
    title: String,
    description: String,
    versionId: String?,
    planDuration: Int,
    numberInstruction: Int,
    numberPersistent: Int,
    numberQuestion: Int,
    numberFloating: Int,
) {
    Row(
        modifier = Modifier.then(modifier),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer),
    ) {
        FlexContent(
            weight = DESCRIPTIVE_WEIGHT,
            justifyContent = JustifyContent.Start,
        ) {
            PlanDescriptiveDetails(
                title = title,
                description = description,
                versionId = versionId,
            )
        }
//        Spacer()
        FlexContent(
            weight = STATISTICS_WEIGHT,
            justifyContent = JustifyContent.Center,
        ) {
            PlanStatisticsContent(
                planDuration = planDuration,
                numberInstruction = numberInstruction,
                numberPersistent = numberPersistent,
                numberQuestion = numberQuestion,
                numberFloating = numberFloating,
            )
        }
    }
}


@Composable
private fun PlanStatisticsContent(
    planDuration: Int,
    numberInstruction: Int,
    numberPersistent: Int,
    numberQuestion: Int,
    numberFloating: Int,
) {
    val totalCommuniques = numberInstruction + numberPersistent + numberQuestion + numberFloating
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(2 * Dimens.listEntryTopDownSpacer),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Row(
            modifier = Modifier,
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(2 * Dimens.listEntryLeftRightSpacer),
        ) {
            PlanStatisticElement(
                underwriteText = planLocalization.current.metadata.duration,
                iconSrc = Assets.IconsUntiled.DURATION,
            ) { SpanText(planLocalization.current.daysCount(planDuration)) }

            PlanStatisticElement(
                underwriteText = planLocalization.current.series.communiques(totalCommuniques),
                iconSrc = Assets.IconsUntiled.COMMUNICATES_ALL,
            ) { SpanText(totalCommuniques.toString()) }
        }
        Row(
            modifier = Modifier,
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer),
        ) {
            PlanStatisticElement(
                underwriteText = planLocalization.current.series.tasks(numberInstruction),
                iconSrc = Assets.IconsUntiled.INSTRUCTION,
            ) { SpanText(numberInstruction.toString()) }

            PlanStatisticElement(
                underwriteText = planLocalization.current.series.persistents(numberPersistent),
                iconSrc = Assets.IconsUntiled.PERSISTENT,
            ) { SpanText(numberPersistent.toString()) }

            PlanStatisticElement(
                underwriteText = planLocalization.current.series.questions(numberQuestion),
                iconSrc = Assets.IconsUntiled.QUESTION,
            ) { SpanText(numberQuestion.toString()) }

            PlanStatisticElement(
                underwriteText = planLocalization.current.series.floatings(numberFloating),
                iconSrc = Assets.IconsUntiled.FLOATING,
            ) { SpanText(numberFloating.toString()) }
        }
    }
}


@Composable
private fun PlanDescriptiveDetails(
    title: String,
    description: String,
    versionId: String?,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.listEntryTopDownSpacer),
    ) {
        UnderwrittenText(
            modifier = Modifier.fillMaxWidth(),
            topAlign = AlignSelf.Start,
            topVariant = ExuraBodyHeaderTextVariant,
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { versionId?.let { SpanText("ID: $it") } },
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .fillMaxWidth()
                    .overflow(Overflow.Hidden)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .textOverflow(TextOverflow.Ellipsis),
            )
        }
        SpanText(
            description,
            modifier = ExuraTextStyle
                .toModifier(ExuraBodySizeTextVariant, ExuraCommentaryTextVariant)
                .fillMaxWidth()
                .lineClamp(4),
        )
    }
}

const val DESCRIPTIVE_WEIGHT = 5
const val STATISTICS_WEIGHT = 3
